import ENDPOINTS from "@config/endpoints";
import { Device } from "@twilio/voice-sdk";
import { createContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import useApp from "src/hooks/useApp";
import { useAuth } from "src/hooks/useAuth";

export const CallContext = createContext();

export function CallProvider({ children }) {
  /** */

  const {
    PM: { profile },
  } = useApp();

  const { axios } = useAuth();

  const [call, setCall] = useState(undefined);
  const [callerID, setCallerID] = useState(profile.default?.callerID);
  const [device, setDevice] = useState(undefined);
  const [accessToken, setAccessToken] = useState(undefined);

  // Get Access Token Mutation
  const GetAccessToken = useMutation(() => axios.get(`${ENDPOINTS.integration.twilio}/sdk/token`), {
    onSuccess: (response) => {
      if (response?.token) setAccessToken(response?.token);
    },
  });

  const _listeners = {
    call: {
      accept: () => setCall((x) => ({ ...x, status: "accept" })),
      reject: () => setCall((x) => ({ ...x, status: "closed" })),
      disconnect: () => setCall((x) => ({ ...x, status: "closed" })),
      cancel: () => setCall((x) => ({ ...x, status: "closed" })),
    },
  };

  const _removePreviousCallListeners = () => {
    call.instance?.off("accept", _listeners.call.accept);
    call.instance?.off("reject", _listeners.call.reject);
    call.instance?.off("disconnect", _listeners.call.disconnect);
    call.instance?.off("cancel", _listeners.call.cancel);
    return;
  };

  // Initiate a New Device
  const _initiateDevice = (accessToken) => {
    if (!accessToken) return console.log("No Access Token");
    console.log("Initiating Device");
    const newDevice = new Device(accessToken);
    setDevice(() => ({ instance: newDevice, status: "pending" }));
    newDevice.on("registered", () => setDevice((x) => ({ ...x, status: "registered" })));
    newDevice.on("unregistered", () => setDevice((x) => ({ ...x, status: "unregistered" })));
    newDevice.on("registering", () => setDevice((x) => ({ ...x, status: "registering" })));
    newDevice.on("tokenWillExpire", () => GetAccessToken.mutate());
    newDevice.on("incoming", (call) => _initiateCall(call, "inbound"));
    newDevice.on("error", (error) => {
      console.log("Error registering the device", error);
    });
    return newDevice.register();
  };

  // Update Device Token
  const _updateDevice = (accessToken) => {
    device?.instance?.updateToken(accessToken);
    return;
  };

  // Initiate a Call (Inbound/Outbound)
  const _initiateCall = (newCall, direction, to = undefined) => {
    if (call) _removePreviousCallListeners();
    newCall.on("accept", _listeners.call.accept);
    newCall.on("reject", _listeners.call.reject);
    newCall.on("disconnect", _listeners.call.disconnect);
    newCall.on("cancel", _listeners.call.cancel);
    setCall({ instance: newCall, direction, status: newCall.status(), to });
    return;
  };

  // AccessToken Callback
  useEffect(() => {
    console.log("AccessToken", accessToken);
    if (!accessToken) return console.log("No Access Token");
    if (!device) _initiateDevice(accessToken); // Create New Device
    else _updateDevice(accessToken); // Update Device Access Token
  }, [accessToken]);

  // First Run
  useEffect(() => {
    console.log("CallerID", callerID);
    GetAccessToken.mutate();
    return () => {
      if (device) {
        device.instance?.removeAllListeners();
        device.instance?.destroy();
      }
    };
  }, [callerID]);

  // :: Methods :: Make an Outbound Call
  const _makeCall = async (callID, to) => {
    if (!callID) return console.log("No CallID");
    try {
      const newCall = await device.instance?.connect({
        params: {
          callID,
        },
      });
      _initiateCall(newCall, "outbound", to);
    } catch (error) {
      console.log(error);
    }
  };

  const value = {
    callerID,
    device: { instance: device?.instance, status: device?.status, callerID },
    call: {
      instance: call?.instance,
      direction: call?.direction,
      status: call?.status,
      active: ["connecting", "open", "pending", "reconnecting", "ringing", "accept"].includes(call?.status),
      callID: call?.instance?.customParameters?.get("callID"),
      displayedNumber: call?.direction === "inbound" ? call?.instance?.parameters?.From : call?.to,
    },
    methods: {
      updateCallerID: (callerID) => (callerID ? setCallerID(callerID) : {}),
      makeCall: (callID, to) => _makeCall(callID, to),
    },
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}
